import React, { useEffect, useState } from 'react';
import { BookUser, Plus, Pencil, Trash2, Star, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { accountAPI } from '../services/api';
import { useT } from '../services/i18n';

const EMPTY = { label: '', first_name: '', last_name: '', phone: '', address: '', complement: '', postal_code: '', city: '', country: 'France', is_default_shipping: false, is_default_billing: false };
const errText = (e, fb) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map(x => x.msg).join(' ') : d || fb; };

const Field = ({ k, label, form, set, ...p }) => (
  <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label><Input data-testid={`address-${k}`} value={form[k]} onChange={set(k)} {...p} /></div>
);

const AddressForm = ({ initial, onClose, onSaved }) => {
  const { t } = useT();
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const saved = initial?.id ? await accountAPI.updateAddress(initial.id, form) : await accountAPI.createAddress(form);
      toast.success(initial?.id ? 'Adresse modifiée' : 'Adresse ajoutée');
      onSaved(saved);
    } catch (e) { toast.error(errText(e, 'Enregistrement impossible')); } finally { setSaving(false); }
  };
  const fp = { form, set };
  return (
    <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-4 mt-4" data-testid="address-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field {...fp} k="label" label={t('acc.addrLabel')} placeholder={t('acc.addrLabelPh')} />
        <Field {...fp} k="phone" label={t('checkout.phone')} placeholder="06 12 34 56 78" />
        <Field {...fp} k="first_name" label={`${t('checkout.firstName')} *`} />
        <Field {...fp} k="last_name" label={`${t('checkout.lastName')} *`} />
        <div className="sm:col-span-2"><Field {...fp} k="address" label="Adresse *" placeholder="12 rue de la République" /></div>
        <div className="sm:col-span-2"><Field {...fp} k="complement" label="Complément" placeholder="Bâtiment, étage…" /></div>
        <Field {...fp} k="postal_code" label={`${t('checkout.postalCode')} *`} maxLength={5} />
        <Field {...fp} k="city" label={`${t('checkout.city')} *`} />
      </div>
      <div className="flex flex-wrap gap-4 mt-3 text-sm">
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" data-testid="address-default-shipping" checked={form.is_default_shipping} onChange={set('is_default_shipping')} className="rounded border-gray-300 text-orange-500" /> {t('acc.defaultShipping')}</label>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" data-testid="address-default-billing" checked={form.is_default_billing} onChange={set('is_default_billing')} className="rounded border-gray-300 text-orange-500" /> {t('acc.defaultBilling')}</label>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose} data-testid="address-cancel">{t('common.cancel')}</Button>
        <Button onClick={save} disabled={saving} data-testid="address-save" className="bg-orange-500 hover:bg-orange-600">{saving && <Loader2 size={16} className="mr-2 animate-spin" />} {t('acc.save')}</Button>
      </div>
    </div>
  );
};

// Customer address book (up to 10 addresses, default shipping/billing)
const AddressBook = () => {
  const { t } = useT();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => accountAPI.addresses().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm('Supprimer cette adresse ?')) return;
    try { await accountAPI.deleteAddress(a.id); toast.success('Adresse supprimée'); load(); } catch (e) { toast.error(errText(e, 'Suppression impossible')); }
  };
  const setDefault = async (a, kind) => { await accountAPI.setDefaultAddress(a.id, kind); load(); };

  if (!items) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-orange-500" /></div>;
  return (
    <div className="bg-white rounded-xl shadow-sm p-6" data-testid="address-book">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><BookUser className="text-orange-500" size={20} /> {t('acc.myAddresses', { n: items.length })}</h2>
        {editing === null && items.length < 10 && <Button size="sm" onClick={() => setEditing({})} data-testid="address-add" className="bg-orange-500 hover:bg-orange-600"><Plus size={15} className="mr-1" /> {t('acc.addAddress')}</Button>}
      </div>
      <p className="text-sm text-gray-500 mb-4">{t('acc.addressIntro')}</p>
      {items.length === 0 && editing === null && <p className="text-sm text-gray-500 border rounded-lg p-6 text-center" data-testid="address-empty"><MapPin className="mx-auto mb-2 text-gray-300" /> {t('acc.noAddress')}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map(a => (
          <div key={a.id} className={`border rounded-lg p-4 relative ${a.is_default_shipping ? 'border-orange-300 bg-orange-50/30' : ''}`} data-testid={`address-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-gray-900">{a.label || 'Adresse'}</p>
              <div className="flex gap-1">
                <button type="button" onClick={() => setEditing(a)} data-testid={`address-edit-${a.id}`} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Pencil size={14} /></button>
                <button type="button" onClick={() => remove(a)} data-testid={`address-delete-${a.id}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
            <p className="text-sm text-gray-700 mt-1">{a.first_name} {a.last_name}<br />{a.address}{a.complement ? <><br />{a.complement}</> : null}<br />{a.postal_code} {a.city}, {a.country}{a.phone ? <><br />{a.phone}</> : null}</p>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              {a.is_default_shipping ? <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium" data-testid={`address-default-shipping-${a.id}`}><Star size={11} /> {t('acc.shipDefault')}</span> : <button type="button" onClick={() => setDefault(a, 'shipping')} data-testid={`address-set-shipping-${a.id}`} className="text-gray-500 hover:text-orange-600 underline">{t('acc.setShipping')}</button>}
              {a.is_default_billing ? <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium" data-testid={`address-default-billing-${a.id}`}><Star size={11} /> {t('acc.billDefault')}</span> : <button type="button" onClick={() => setDefault(a, 'billing')} data-testid={`address-set-billing-${a.id}`} className="text-gray-500 hover:text-blue-600 underline">{t('acc.setBilling')}</button>}
            </div>
          </div>
        ))}
      </div>
      {editing !== null && <AddressForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

export default AddressBook;
